/**
 * @file        src/lib/useBranding.js
 * @description Tenant branding for the admin CHROME — the pre-auth screens
 *              (login, forgot password) and the sidebar. Reads
 *              GET /site-settings/public, which the backend scopes to the tenant
 *              behind the current Host header — so each tenant domain gets its
 *              own configured logo with no tenant name or domain in this code.
 *              Every failure path returns nulls so the caller falls back to the
 *              bundled default logo and the auth form is never blocked.
 *
 *              Deliberately the PUBLIC endpoint even post-login: one cache entry
 *              serves both sides of the auth boundary, and the sidebar then needs
 *              no token-timing special-casing on a hard refresh.
 * @author      Kinetika Labs
 * @created     2026-07-31
 * @changelog
 *   - 2026-07-31 Claude — Initial (per-tenant logo on the admin login page).
 *   - 2026-07-31 Claude — markUrl (favicon) added for the collapsed sidebar rail.
 */
import { useQuery } from '@tanstack/react-query';
import { get } from '../api/client';
import { qk } from '../api/queryKeys';

/**
 * @returns {{ logoUrl: string|null, markUrl: string|null, name: string|null }}
 * `logoUrl` is the wide logo, `markUrl` the square mark (the configured favicon,
 * used where the wide logo does not fit). Either is null when the tenant has not
 * configured it, on the super-admin host, or on any network/HTTP error — render
 * the bundled default in that case.
 */
export function useBranding() {
  const { data } = useQuery({
    queryKey: qk.siteSettings.publicBranding,
    queryFn: () => get('/site-settings/public'),
    staleTime: 10 * 60 * 1000,
    // Branding is decorative: never retry and never surface an error state, so a
    // slow or failing call can't delay or break the sign-in form.
    retry: false,
    refetchOnWindowFocus: false,
  });

  const b = data?.data;
  return {
    logoUrl: b?.logo_url || null,
    markUrl: b?.favicon_url || null,
    name: b?.name || null,
  };
}
